<template>
  <article class="view-admin-organization">
    <template v-if="data.organization">
      <div class="breadcrump-trail">
        <router-link :to="{ name: 'admin-home' }"> Admin </router-link> /
        <router-link :to="{ name: 'admin-organizations-list' }">
          Organisationen
        </router-link>
        /
        {{ data.organization.id }}
      </div>

      <form @submit.prevent="updateSubscription">
        <h1>{{ data.organization.name }}</h1>

        <input-text
          v-model:value="validUntil"
          class="mb-2"
          :label="$t('labels.validUntil')"
          type="date"
        />

        <input-checkbox
          v-model:value="data.renewAutomatically"
          :label="$t('labels.automaticRenewal')"
        />

        <input-number
          v-model:value="data.seats"
          :label="$t('words.user', 2)"
          :min="1"
          :step="1"
          class="mt-2"
          :allow-null="true"
        />

        <button
          type="submit"
          class="button mt-4"
          :disabled="!orgChanged || saving"
        >
          <template v-if="saving">
            {{ $t("messages.saving") }}
          </template>
          <template v-else-if="!orgChanged">
            {{ $t("labels.saved") }}
          </template>
          <template v-else>
            {{ $t("labels.save") }}
          </template>
        </button>
      </form>

      <hr class="mv-7" />

      <h3>{{ $t("words.member", 3) }}</h3>
      <member-list
        :members="members"
        :can-manage-members="true"
        :can-see-emails="true"
        :roles="data.organization.roles"
        :assignable-role-codes="data.organization.roles.map((r) => r.roleCode)"
        :max-members="data.organization.licenseLimit"
        :can-set-valid-until="true"
        @update="updateMember"
        @delete="removeMember"
      />

      <member-invitation-popup
        :visible="memberInvitationPopupVisible"
        :max-invitations="data.organization.licenseLimit"
        :existing-emails="members.map((m) => m.email)"
        :invite-function="inviteMembers"
        :autocomplete-options="[]"
        :roles="data.organization.roles"
        :can-set-valid-until="true"
        @close="memberInvitationPopupVisible = false"
      />
      <button
        class="button mt-4"
        type="button"
        @click="memberInvitationPopupVisible = true"
      >
        {{ $t("actions.inviteMembers") }}
      </button>

      <hr class="mv-7" />

      <h3>{{ $t("words.email", 3) }}</h3>
      Kommende Verlängerung:
      {{ formatDateTime(data.organization.subscription.notifiedOfUpcomingRenewal) }}<br />

      Kommender Ablauf:
      {{ formatDateTime(data.organization.subscription.notifiedOfUpcomingExpiration) }}<br />

      Abgelaufen: {{ formatDateTime(data.organization.subscription.notifiedOfExpiration) }}

      <h3>{{ $t("labels.dates") }}</h3>
      <ul>
        <li>Letzte Verlängerung: {{ formatDateString(data.organization.subscription.lastRenewed) }}</li>
        <li>
          Verlängerung deaktiviert am:
          {{ formatDateTime(data.organization.subscription.renewalCanceledAt) }}
        </li>
        <li>
          Abgelaufen am {{ formatDateTime(data.organization.subscription.expiredAt) }}
          <button
            v-if="data.organization.subscription.expiredAt"
            type="button"
            class="button_secondary"
            @click="resetOrganizationExpiration()"
          >
            {{ $t("actions.reset") }}
          </button>
        </li>
      </ul>

      <hr class="mv-7" />
      <button type="button" class="button button_secondary button--danger" @click="deleteOrganization">
        {{ $t("actions.deleteOrganization") }}
      </button>
    </template>
    <template v-else>...</template>
  </article>
</template>

<script setup lang="ts">
import { ref, computed, reactive } from "vue";
import { onBeforeRouteUpdate, useRouter } from "vue-router";
import { showError } from "@core/ui/alert/alert";
import { formatDate } from "@/i18n";

import { useAdmin } from "./useAdmin";

import InputNumber from "@core/input/InputNumber.vue";
import InputText from "@core/input/InputText.vue";
import InputCheckbox from "@core/input/InputCheckbox.vue";

import MemberList from "../members/MemberList.vue";
import MemberInvitationPopup from "../members/MemberInvitationPopup.vue";

import type { OrganizationMember } from "@/api/Organization";
import type { AdminOrganization, AdminSubscription } from "./AdminTypes";
import { sortBy } from "lodash-es";

const props = defineProps<{
  organizationId: string;
}>();

onBeforeRouteUpdate(() => {
  getOrganization();
});

const router = useRouter();
const adminAPI = useAdmin();

const saving = ref(false);
const memberInvitationPopupVisible = ref(false);

const data = reactive({
  organization: null as null | AdminOrganization,
  
  seats: 1 as number | null,
  workspaceLimit: 1 as number | null,
  renewAutomatically: true,
  validUntilDate: null as null | Date,
});

getOrganization();


const orgChanged = computed(() => {
  if (data.organization == null) return false;

  return (
    data.organization.licenseLimit != data.seats ||
    data.organization.workspaceLimit != data.workspaceLimit ||
    data.organization.subscription.renewAutomatically != data.renewAutomatically ||
    orgValidUntilDate.value?.toISOString()?.substring(0, 10) !=
      validUntil.value
  );
});
const orgValidUntilDate = computed(() => {
  if (data.organization == null) return null;
  if (!data.organization.subscription.validUntil) return null;
  return new Date(data.organization.subscription.validUntil);
});

const validUntil = computed({
  get() {
    if (data.validUntilDate == null) return undefined;
    return data.validUntilDate.toISOString().substring(0, 10);
  },
  set(value: string | undefined) {
    if (value == null) data.validUntilDate = null;
    else {
      data.validUntilDate = new Date(value);
    }
  }
});

const members = computed(() => {
  if (data.organization == null) return [];
  return sortBy(data.organization.members ?? [], "email");
});


async function getOrganization() {
  try {
    const org = await adminAPI.get<AdminOrganization>(
      `/organizations/${props.organizationId}`
    );

    data.seats = org.licenseLimit;
    data.workspaceLimit = org.workspaceLimit;
    data.renewAutomatically = org.subscription.renewAutomatically;
    if (org.subscription.validUntil) {
      data.validUntilDate = new Date(org.subscription.validUntil);
    } else {
      data.validUntilDate = null;
    }

    data.organization = org;
  } catch (error) {
    showError(error as Error);
  }
}

async function updateSubscription() {
  const subscriptionData = {
    seats: data.seats,
    validUntil: data.validUntilDate?.toISOString(),
    renewAutomatically: data.renewAutomatically
  };
  saving.value = true;
  try {
    if (data.organization == null) {
      throw new Error("Organization not found");
    }

    const sub = await adminAPI.patch<AdminSubscription>(
      `/users/${data.organization?.owner.id}/subscriptions/${data.organization.subscription.id}`,
      {
        data: subscriptionData
      }
    );
    data.organization.subscription = sub;
    console.log("patched", sub);
  } catch (error) {
    showError(error as Error);
  } finally {
    saving.value = false;
  }
}

async function resetOrganizationExpiration() {
  try {
    if (data.organization == null) {
      throw new Error("Organization not found");
    }

    if (data.organization.subscription == null) {
      throw new Error("Subscription not found");
    }

    const sub = await adminAPI.patch<AdminSubscription>(
      `/users/${data.organization?.owner.id}/subscriptions/${data.organization.subscription.id}`,
      {
        data: { expiredAt: null }
      }
    );

    data.organization.subscription = sub;
  } catch (error) {
    showError(error as Error);
  }
}

async function deleteOrganization() {
  if (!confirm("Wirklich die Organisation inklusive aller Modelle und Lizenzen löschen??")) return;

  try {
    await adminAPI.destroy(`/organizations/${props.organizationId}`);
    router.push({ name: "admin-organizations-list" });
  } catch (error) {
    showError(error as Error);
  }
}

// ======================
// Member management

async function updateMember(memberData: { id: number }) {
  try {
    const updatedMember = await adminAPI.patch<OrganizationMember>(
      `/organizations/${props.organizationId}/members`,
      {
        data: memberData
      }
    );

    if (updatedMember == null) {
      return;
    }

    if (updatedMember.roleCode == "OWNER") {
      // reload the organization, because the owner has changed
      await getOrganization();
      return;
    }
    
    const members = data.organization?.members || [];
    const index = members.findIndex((m) => m.id == memberData.id);
    members.splice(index, 1, updatedMember);
  } catch (error) {
    showError(error as Error);
  }
}

async function removeMember(memberData: { id: number }) {
  try {
    await adminAPI.destroy(
      `/organizations/${props.organizationId}/members`,
      {
        data: memberData
      }
    );

    const members = data.organization?.members || [];
    const index = members.findIndex((m) => m.id == memberData.id);
    members.splice(index, 1);
  } catch (error) {
    showError(error as Error);
  }
}

async function inviteMembers(member: ({ user: { email: string } })[]) {
  return adminAPI.post<OrganizationMember[]>(
    `/organizations/${props.organizationId}/members`,
    { data: member }
  ).then((members) => {
    if (data.organization == null) {
      throw new Error("Organization not found");
    }

    data.organization.members = data.organization.members.concat(members);
  });
}

function formatDateString(dateString?: string | null, format = "D") {
  if (dateString == null || dateString == "") return "—";
  const date = new Date(dateString);
  return formatDate(date, format);
}

function formatDateTime(dateString?: string | null) {
  if (dateString == null || dateString == "") return "—";
  return formatDateString(dateString, "f");
}
</script>

<style lang="scss">
.view-admin-organization {
  width: 100%;
  padding: 2rem;
}
</style>
